import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import tick from './tick.png';
import archery from './archery.png';
import gun from './rightimg3.png';
import img1 from './prob1.jpg';
import img2 from './prob2.jpg';
import res1 from './resource1.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowUp, 
  faShoppingCart, 
  faCube, 
  faRobot, 
  faShieldAlt,
  faCheckCircle,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import "./About.css";

const About = () => {
  const [showScrollUpButton, setShowScrollUpButton] = useState(false);
  const observerRef = useRef(null);

  // Optimized IntersectionObserver with useCallback
  useEffect(() => {
    const options = {
      threshold: 0.15,
      rootMargin: "0px 0px -100px 0px"
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
          observerRef.current?.unobserve(entry.target);
        }
      });
    }, options);

    const elements = document.querySelectorAll(
      ".fromtop-anim, .fade-effect, .aboutrightsec, .left-img-effect, .right-img-effect, .stat-card, .feature-card, .learning-about, .about-admin-box, .cta-section, .status, .prob-content, .prob-content2"
    );

    elements.forEach((el) => observerRef.current?.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Throttled scroll handler for better performance
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setShowScrollUpButton(window.scrollY > 300);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const stats = [
    { 
      icon: faShoppingCart, 
      number: "10K+", 
      label: "Products Available",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    { 
      icon: faCube, 
      number: "5000+", 
      label: "3D Models",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    { 
      icon: faRobot, 
      number: "95%", 
      label: "AI Accuracy",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    { 
      icon: faShieldAlt, 
      number: "100%", 
      label: "Verified Products",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    }
  ];

  const features = [
    {
      icon: "🛍️",
      title: "E-commerce with 3D Models",
      description: "Browse products with photorealistic 3D models for 360° viewing and detailed inspection.",
      color: "#667eea"
    },
    {
      icon: "📱",
      title: "AR Product Preview",
      description: "Explore products in your space using augmented reality before making a purchase.",
      color: "#f5576c"
    },
    {
      icon: "🤖",
      title: "AI Recommendations",
      description: "Get personalized product suggestions powered by advanced machine learning algorithms.",
      color: "#4facfe"
    },
    {
      icon: "🔒",
      title: "Blockchain Security",
      description: "Every product is verified and secured using blockchain technology for authenticity.",
      color: "#43e97b"
    }
  ];

  const goals = [
    {
      img: tick,
      title: "Immersive Shopping Experience",
      description: "Build an e-commerce platform providing an immersive shopping experience with photorealistic 3D models and 360° viewer for detailed product inspection.",
      status: "completed"
    },
    {
      img: archery,
      title: "Interactive AR Technology",
      description: "Make products interactive in a real environment rather than just demonstrating static 3D models using ML-AI and augmented reality technology.",
      status: "in-progress"
    },
    {
      img: gun,
      title: "Blockchain & AI Integration",
      description: "Integrate blockchain verification for product authenticity and AI-powered recommendations for personalized shopping experiences.",
      status: "in-progress"
    }
  ];

  return (
    <main className="about-page">
      {/* Hero Banner */}
      <section className="about-banner" aria-label="Hero section">
        <div className="about-banner-overlay"></div>
        <div className="about-banner-front">
          <div className="aboutleft">
            <h1 className="aboutleftsec fromtop-anim">
              AR Bazaar
              <span className="brand-tagline">The Future of Shopping</span>
            </h1>
            <p className="abouleftseccont fade-effect">
              Experience immersive augmented reality shopping powered by cutting-edge AI and blockchain technology. Transform the way you shop online.
            </p>
            <div className="cta-group fade-effect">
              <Link to="/wishlist" className="cta-button cta-primary">
                <FontAwesomeIcon icon={faRocket} />
                Start Shopping
              </Link>
              <Link to="/" className="cta-button cta-secondary">
                View Products
              </Link>
            </div>
          </div>
          <div className="aboutrightsec">
            <img src={gun} alt="AR Shopping Experience" className="hero-image" loading="lazy" />
            <div className="hero-decoration"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section" aria-label="Platform statistics">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <article 
              key={index} 
              className="stat-card"
              style={{ '--card-gradient': stat.gradient }}
            >
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={stat.icon} className="stat-icon" />
              </div>
              <h3 className="stat-number">{stat.number}</h3>
              <p className="stat-label">{stat.label}</p>
              <div className="stat-decoration"></div>
            </article>
          ))}
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section" aria-labelledby="challenge-heading">
        <h2 id="challenge-heading" className="second-head fromtop-anim">
          The Challenge
        </h2>
        <div className="prob-box">
          <div className="prob-content left-img-effect">
            <div className="section-badge">Problem</div>
            <h3 className="about-prob-header">Traditional E-commerce Limitations</h3>
            <p className="about-prob-sol">
              Traditional e-commerce platforms often lack immersive product experiences, leaving customers uncertain about how items will look, fit, and function. This gap in realistic visualization results in higher return rates due to unmet expectations.
            </p>
            <ul className="problem-list">
              <li><FontAwesomeIcon icon={faCheckCircle} /> Limited product visualization</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Higher return rates</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Reduced customer confidence</li>
            </ul>
          </div>
          <div className="prob-img-wrapper left-img-effect">
            <img src={img1} alt="E-commerce challenges" className="prob-img" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section" aria-labelledby="solution-heading">
        <h2 id="solution-heading" className="second-head fromtop-anim">
          Our Solution
        </h2>
        <div className="prob-box2">
          <div className="prob-img-wrapper right-img-effect">
            <img src={img2} alt="AR/VR solution" className="prob-img2" loading="lazy" />
          </div>
          <div className="prob-content2 right-img-effect">
            <div className="section-badge solution-badge">Solution</div>
            <h3 className="about-prob-header">AR/VR Shopping Revolution</h3>
            <p className="about-prob-sol2">
              AR Bazaar offers a cutting-edge virtual shopping experience using Augmented Reality (AR) and Virtual Reality (VR). This platform lets customers visualize products in their space and try them on virtually, ensuring an interactive view.
            </p>
            <ul className="solution-list">
              <li><FontAwesomeIcon icon={faCheckCircle} /> Real-time AR visualization</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> 360° product inspection</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> AI-powered recommendations</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Reduced return rates</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="features-section" aria-labelledby="features-heading">
        <h2 id="features-heading" className="second-head fromtop-anim">
          Key Features
        </h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <article 
              key={index} 
              className="feature-card"
              style={{ '--feature-color': feature.color }}
            >
              <div className="feature-icon-wrapper">
                <span className="feature-icon">{feature.icon}</span>
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <div className="feature-hover-effect"></div>
            </article>
          ))}
        </div>
      </section>

      {/* Goals Section */}
      <section className="about-goals-section" aria-labelledby="goals-heading">
        <h2 id="goals-heading" className="second-head fromtop-anim">
          Our Goals & Milestones
        </h2>
        <div className="about-goals-box">
          {goals.map((goal, index) => (
            <article key={index} className="status fade-effect">
              <div className="status-img-wrapper">
                <img src={goal.img} alt={goal.title} className="status-img" loading="lazy" />
              </div>
              <div className="status-badge">{goal.status === 'completed' ? 'Completed' : 'In Progress'}</div>
              <h3 className="status-header">Goal {index + 1}</h3>
              <h4 className="status-title">{goal.title}</h4>
              <p className="status-points">{goal.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Technology Stack */}
      <section className="tech-section" aria-labelledby="tech-heading">
        <h2 id="tech-heading" className="second-head fromtop-anim">
          Technology Stack
        </h2>
        <div className="learning-about-block">
          <article className="learning-about fade-effect">
            <div className="tech-icon">⚛️</div>
            <h3>Frontend Technologies</h3>
            <p>
              Built with modern web technologies including <strong>React</strong>, <strong>Three.js</strong>, and <strong>WebGL</strong> to create immersive, interactive 3D experiences. Our frontend stack enables photorealistic rendering and smooth animations.
            </p>
            <ul className="tech-list">
              <li>React 18+ with Hooks</li>
              <li>Three.js for 3D rendering</li>
              <li>WebXR for AR experiences</li>
              <li>Framer Motion animations</li>
            </ul>
          </article>

          <article className="learning-about fade-effect">
            <div className="tech-icon">🚀</div>
            <h3>Backend & AI</h3>
            <p>
              Powered by <strong>Node.js</strong>, <strong>Express</strong>, and <strong>AI/ML algorithms</strong>. Our platform delivers intelligent product recommendations, real-time AR rendering, and blockchain-verified authenticity.
            </p>
            <ul className="tech-list">
              <li>Node.js & Express API</li>
              <li>TensorFlow.js for AI</li>
              <li>Blockchain integration</li>
              <li>PostgreSQL database</li>
            </ul>
          </article>
        </div>
      </section>

      {/* Resources Section */}
      <section className="resources-section" aria-labelledby="resources-heading">
        <h2 id="resources-heading" className="second-head fromtop-anim">
          Resources & Documentation
        </h2>
        <p className="resources-subtitle fade-effect">
          Explore our comprehensive documentation and learning resources
        </p>
        <div className="about-res-grid fade-effect">
          <a 
            href="https://threejs.org/docs/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="resource-card"
            aria-label="Three.js documentation"
          >
            <img src={res1} alt="Three.js Documentation" className="res1-img" loading="lazy" />
            <div className="resource-overlay">
              <h4>Three.js Docs</h4>
              <p>Comprehensive 3D library documentation</p>
            </div>
          </a>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section" aria-labelledby="team-heading">
        <h2 id="team-heading" className="second-head fromtop-anim">
          Meet the Team
        </h2>
        <div className="about-admin-box fade-effect">
          <div className="admin-img-wrapper">
            <img 
              src="https://avatars.githubusercontent.com/u/83642826?v=4" 
              alt="Md Faizaan Alam - Founder" 
              className="about-admin-img"
              loading="lazy"
            />
            <div className="admin-status-indicator"></div>
          </div>
          <div className="about-admin">
            <h3>Md Faizaan Alam</h3>
            <p className="admin-role">Founder & Lead Developer</p>
            <p className="admin-bio">
              Passionate about creating immersive web experiences with AR/VR technology
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section fade-effect" aria-labelledby="cta-heading">
        <div className="cta-content">
          <h2 id="cta-heading">Ready to Experience the Future of Shopping?</h2>
          <p>Join thousands of satisfied customers using AR technology for smarter purchases.</p>
          <Link to="/wishlist" className="cta-button-large">
            <FontAwesomeIcon icon={faRocket} />
            Explore Products Now
          </Link>
        </div>
        <div className="cta-decoration"></div>
      </section>

      {/* Scroll to Top Button */}
      {showScrollUpButton && (
        <button 
          className="scroll-up-button" 
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      )}
    </main>
  );
};

export default About;
